import Link from 'next/link';
import { useState } from 'react';
import { Share } from 'lucide-react';
import { displayName } from '../../../lib/artifact-file';
import { creatorOf, folderCountLabel, folderTitle, folderWorkspaceName, pathSegments, type FileFolderRow } from '../../../lib/file-browser';
import { previewImageUrl } from '../../../lib/artifact-image';
import { formatConversationDate } from '../../../lib/conversations';
import { useTranslation } from '../../../lib/i18n';
import { FileCreatedBy } from '../FileCreatedBy';
import { FileGraphic } from '../FileArtifactCard';
import { FilesSpaceMark } from './FilesSpaceMark';
import { FolderGlyph } from './FolderGlyph';
import { FileActionsSheet } from './FileActionsSheet';
import { FileRowMenu } from './FileRowMenu';
import { Card, IconButton, type OverflowMenuAction } from '../ui';
import styles from './FilesGrid.module.css';
import type { FilesRow } from './files-row';

// The same files as tiles. The thumbnail is `FileGraphic` — the SAME graphic the workspace screen's file cards
// use (an image file previews itself, everything else gets its kind glyph on a neutral well), so a file looks
// the same wherever you meet it rather than being drawn twice.
//
// Everything except the layout is shared with the list: the same rows, the same actions, the same share button,
// the same click behaviour (a real link underneath, plain click opens the viewer).
//
// **The card reads top-down: NAME → thumbnail → Space → who and when** (Figma, redesign §10). Three things about
// that order are deliberate:
//
//   - the name is ABOVE the thumbnail, with the share glyph and the ⋮ on its line, in normal flow. They used to
//     float over the thumbnail's corner on a blurred chip, which covered the very picture the tile exists to
//     show and read as chrome pasted onto the image rather than as the card's own header.
//   - the SIZE is gone and the DATE has arrived. A byte count is the least useful thing a tile can say about a
//     file; when it was made, and by whom, is what the meta line is for.
//   - the Space is a BADGE beside its name, not a bare caption — the same mark the list's Workspace column
//     draws, which is what lets a tile say which Space it belongs to inside a merged-root folder (§13).
//
// ## ⚠️ THE PHONE TILE IS A DIFFERENT CARD, off its own nodes (`16001:435591` file, `16001:435271` folder)
//
// Not the wide tile made smaller — a different content set, so the DOM carries both meta blocks and the one
// shared container query (`@container files (max-width: 600px)`) picks which is drawn. The phone card says:
//
//   NAME + ⋮ → thumbnail → ONE meta row: who made it (or how much is in it) at the leading edge, the
//   Space's own 20px avatar at the trailing edge.
//
// So on a phone there is **no date**, **no "Workspace name" text line** and **no share glyph** — three
// things the wide tile draws and this one does not. (The 2-up container frames `15918:427834` /
// `15918:427779` still draw a Workspace-name line; the real instances do not, and the instances win.)
//
// Two reasons the two blocks are separate DOM rather than one restyled by the query: the attribution mark
// is **15px wide, 10px narrow** and the Space avatar moves from its own line into the meta row — a mark's
// size is a React prop and a query cannot reach one, and no amount of CSS moves a child between two
// parents. It is the same call `FilesTable` already made for its own two Space marks (`.spaceWide` /
// `.spaceNarrow`).
export function FilesGrid({
  rows,
  folders,
  narrowPane,
  onOpenFolder,
  showFolderWorkspace = false,
  workspaceNames = {},
  onOpen,
  onShare,
  shareLabelKey = 'files.access.action',
  actionsFor,
  onRowMenu,
  selectedName,
}: {
  rows: FilesRow[];
  /**
   * Is the enclosing `files` size container at or under `FILES_FOLD_BREAKPOINT_PX`? See the full note on
   * `FilesTable`'s identical prop — same boolean, same two decisions (the tile's share glyph, and
   * sheet-vs-popover), same reason it arrives as a prop rather than a hook call: the host that provides
   * the `files` container is the only thing that can measure it, and a second reader of the width is
   * what let the glyph and the menu item disagree.
   */
  narrowPane: boolean;
  /** The current folder's subfolders — tiles in the SAME grid, because a folder and a file are both things
   *  sitting in this directory, and a row of folders above a grid of tiles reads as two separate lists. */
  folders: FileFolderRow[];
  onOpenFolder: (path: string) => void;
  /** Name each folder's owning Space — the merged tree only. */
  showFolderWorkspace?: boolean;
  workspaceNames?: Record<string, string>;
  onOpen: (row: FilesRow) => void;
  onShare: (row: FilesRow) => void;
  /** See `FilesTable` — the chat panel's glyph downloads rather than granting. */
  shareLabelKey?: string;
  actionsFor: (row: FilesRow) => OverflowMenuAction[][];
  onRowMenu?: (row: FilesRow) => void;
  selectedName?: string;
}) {
  const { t } = useTranslation();
  // The tile whose actions SHEET is open. Held beside the tiles rather than inside one — see the same
  // note in `FilesTable`: a portal does not escape React's event bubbling.
  const [sheetRow, setSheetRow] = useState<FilesRow | null>(null);
  return (
    <div className={styles.grid}>
      {folders.map((folder) => (
        <FolderTile
          key={folder.path}
          folder={folder}
          workspaceNames={workspaceNames}
          showWorkspace={showFolderWorkspace}
          onOpen={onOpenFolder}
        />
      ))}
      {rows.map((row) => {
        const creator = creatorOf(row.item);
        const space = showFolderWorkspace ? row.workspaceName : undefined;
        const date = row.item.updatedDate ? formatConversationDate(row.item.updatedDate) : null;
        // Does this file PREVIEW itself? Only a cover image does — a PDF page, a converted `.docx` and a
        // text miniature are all drawn ON the neutral well, and the folder mark is too. The phone node
        // (`16001:435591`) frames a real preview in a 1px hairline and leaves the well-with-a-glyph
        // unbordered, so the well needs to know which of the two it is holding. Same predicate
        // `FileGraphic` itself branches on, so the two can never disagree about what a preview is.
        const preview = previewImageUrl(row.item) != null;
        return (
          <Card
            key={row.key}
            variant="sunken"
            size="lg"
            selected={selectedName != null && row.item.name === selectedName}
            className={styles.tile}
            disablePadding
          >
            {/* The card's header: the name, then the two controls, in normal flow above the thumbnail. */}
            <span className={styles.head}>
              <Link
                href={row.href}
                as={row.as}
                className={`${styles.nameGroup} ${styles.nameLink}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  onOpen(row);
                }}
              >
                <span className={styles.name}>{displayName(row.item)}</span>
              </Link>
              <span className={styles.actions}>
                {/* lucide `Share` (the tray with the arrow leaving it), not `Share2` — one share mark across
                    web, iOS and Android.

                    ⚠️ WIDE PANE ONLY, and `!narrowPane` is HALF OF ONE CONDITION whose other half is
                    `FilesBrowser` handing `onGrantAccess` to the menu builder when `narrowPane` — see the
                    long note on the same gate in `FilesTable`. It used to be `display: none` inside
                    `@container files (max-width: 600px)` while the item was gated on the VIEWPORT, so a
                    squeezed pane on a wide window had neither. One boolean makes that unreachable. */}
                {!narrowPane && (
                  <IconButton
                    size="medium"
                    aria-label={t(shareLabelKey)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onShare(row);
                    }}
                  >
                    <Share size={16} aria-hidden />
                  </IconButton>
                )}
                {/* Wide → the same denser popover the list rows draw; narrow → the bottom sheet
                    below, off the SAME boolean that decided the glyph. See `FileRowMenu`. */}
                <FileRowMenu
                  narrowPane={narrowPane}
                  groups={actionsFor(row)}
                  ariaLabel={t('agentCreations.table.options')}
                  onOpen={() => onRowMenu?.(row)}
                  onSheet={() => setSheetRow(row)}
                />
              </span>
            </span>
            <Link
              href={row.href}
              as={row.as}
              className={styles.tileLink}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                onOpen(row);
              }}
            >
              <FileGraphic
                item={row.item}
                basePath={row.basePath}
                thumbHeight={104}
                className={preview ? `${styles.thumb} ${styles.thumbPreview}` : styles.thumb}
              />
              <span className={styles.body}>
                {/* ── THE WIDE TILE'S meta (Figma `15702:130963`), hidden on a phone ──────────────── */}
                {/* The Space, as its own badge beside its name — the same mark the list column draws. */}
                {space && (
                  <span className={styles.spaceLine}>
                    <FilesSpaceMark id={row.item.workspaceId} name={space} />
                    <span className={styles.spaceName}>{space}</span>
                  </span>
                )}
                <span className={styles.meta}>
                  {/* 15px mark (Figma `15702:130963`) — it was 14. */}
                  {creator && <FileCreatedBy creator={creator} size={15} />}
                  {date && <span className={styles.metaText}>{date}</span>}
                </span>
                {/* ── THE PHONE TILE'S meta (Figma `16001:435591`), hidden on a wide one ───────────
                    ONE row: who made it at the leading edge, the Space's own avatar at the trailing one.
                    No date — the phone node draws none, and a tile 144px wide spends the line it has on
                    the writer rather than on a timestamp the file's own screen already carries. The mark
                    is 10 here against the wide tile's 15, which is why this is a second element and not
                    the same one restyled. */}
                <span className={styles.phoneMeta}>
                  {creator && <FileCreatedBy creator={creator} size={10} />}
                  {/* 20px, and rendered only when there IS a Space to name — `FilesSpaceMark` returns
                      nothing without both an id and a name, which is what a per-Space Files page has. */}
                  <FilesSpaceMark id={row.item.workspaceId} name={space} />
                </span>
              </span>
            </Link>
          </Card>
        );
      })}

      {/* ONE sheet for the whole grid, outside every tile — see `sheetRow` above. */}
      {sheetRow && (
        <FileActionsSheet
          open
          onClose={() => setSheetRow(null)}
          title={displayName(sheetRow.item)}
          groups={actionsFor(sheetRow)}
        />
      )}
    </div>
  );
}

/** A folder as a tile: the same well a file's thumbnail fills, with the two-tone folder mark in the owning
 *  Space's colours instead of a preview, its name on top, and how much is anywhere beneath it. No agent and no
 *  date — a folder has neither. */
function FolderTile({
  folder,
  workspaceNames,
  onOpen,
  showWorkspace = false,
}: {
  folder: FileFolderRow;
  workspaceNames: Record<string, string>;
  onOpen: (path: string) => void;
  /** Name the owning Space under the folder — the merged tree only, where two Spaces can each have an "images". */
  showWorkspace?: boolean;
}) {
  const { t } = useTranslation();
  const segments = pathSegments(folder.path);
  const title = folderTitle(folder.path, workspaceNames, t);
  const count = folderCountLabel(folder, t);
  const space = showWorkspace ? folderWorkspaceName(folder.path, workspaceNames) : '';
  return (
    <Card variant="sunken" size="lg" className={styles.tile} disablePadding>
      <div
        className={styles.tileLink}
        role="button"
        tabIndex={0}
        aria-label={title}
        onClick={() => onOpen(folder.path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(folder.path);
          }
        }}
      >
        <span className={styles.head}>
          {/* The same `nameGroup` box the file tile's name link is — a folder's title used to sit flush
              against the card's own 8 while a file's was inset 8 inside it, i.e. two different leading
              edges for the same line at the same width. */}
          <span className={styles.nameGroup}>
            <span className={styles.name}>{title}</span>
          </span>
        </span>
        <span className={`${styles.thumb} ${styles.folderGraphic}`}>
          <FolderGlyph workspaceId={segments[0]} width={75} />
        </span>
        <span className={styles.body}>
          {/* ── THE WIDE TILE'S meta (Figma `15702:130963`), hidden on a phone ──────────────────── */}
          {space && (
            <span className={styles.spaceLine}>
              <FilesSpaceMark id={segments[0]} name={space} />
              <span className={styles.spaceName}>{space}</span>
            </span>
          )}
          <span className={styles.meta}>
            <span className={styles.metaText}>{count}</span>
          </span>
          {/* ── THE PHONE TILE'S meta (Figma `16001:435271`), hidden on a wide one ────────────────
              The same one row the file tile draws, with the count where the writer would be — and in the
              QUIET ink where a file's writer is in the primary one. That asymmetry is the phone LIST
              row's too (`FilesTable`), and it is the design: a name is a thing, a count is a fact about
              one. */}
          <span className={styles.phoneMeta}>
            <span className={styles.phoneCount}>{count}</span>
            <FilesSpaceMark id={segments[0]} name={space} />
          </span>
        </span>
      </div>
    </Card>
  );
}
